import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, AlertCircle, CheckCircle } from 'lucide-react';
import { db } from '../../firebase/config';

const ConnectionStatus: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [firestoreStatus, setFirestoreStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Test Firestore connection
    const testConnection = async () => {
      try {
        setFirestoreStatus('connecting');
        // Try a simple read operation using the v9 API
        const { collection, getDocs, limit, query } = await import('firebase/firestore');
        const testCollection = collection(db, '_test');
        const testQuery = query(testCollection, limit(1));
        await getDocs(testQuery);
        setFirestoreStatus('connected');
        setLastError(null);
      } catch (error) {
        setFirestoreStatus('error');
        setLastError(error instanceof Error ? error.message : 'Unknown error');
      }
    };

    testConnection();

    // Test connection every 30 seconds
    const interval = setInterval(testConnection, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  if (process.env.NODE_ENV === 'production') {
    return null; // Don't show in production
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 z-50">
      <div className="flex items-center gap-2 text-sm">
        <div className="flex items-center gap-1">
          {isOnline ? (
            <Wifi className="w-4 h-4 text-green-500" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-500" />
          )}
          <span className={isOnline ? 'text-green-600' : 'text-red-600'}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>
        
        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
        
        <div className="flex items-center gap-1">
          {firestoreStatus === 'connected' && <CheckCircle className="w-4 h-4 text-green-500" />}
          {firestoreStatus === 'error' && <AlertCircle className="w-4 h-4 text-red-500" />}
          {firestoreStatus === 'connecting' && (
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
          <span className={
            firestoreStatus === 'connected' ? 'text-green-600' :
            firestoreStatus === 'error' ? 'text-red-600' :
            'text-blue-600'
          }>
            Firestore: {firestoreStatus}
          </span>
        </div>
      </div>
      
      {lastError && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 max-w-xs">
          {lastError}
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;
